import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/api';
import ImageUpload from './ImageUpload';
import DocumentUpload from './DocumentUpload';

const ResearchEdit = () => {
  const [researchData, setResearchData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    bannerTitle: '',
    bannerSubtitle: '',
    bannerImage: '',
    publicationsTitle: '',
    publicationsDescription: '',
    facultyPublications: [],
    iicTitle: '',
    iicDescription: '',
    iicAbout: '',
    iicInitiatives: [],
    iicProjectIdeas: '',
    iicPatentsFiled: '',
    iicMembers: [],
    facilitiesTitle: '',
    facilitiesDescription: '',
    researchFacilities: []
  });

  useEffect(() => {
    fetchResearchData();
  }, []);

  const fetchResearchData = async () => {
    try {
      setLoading(true);
      const response = await apiService.getResearch();
      const data = response.data;
      
      setResearchData(data);
      setFormData({
        bannerTitle: data.bannerTitle || '',
        bannerSubtitle: data.bannerSubtitle || '',
        bannerImage: data.bannerImage || '',
        publicationsTitle: data.publicationsTitle || '',
        publicationsDescription: data.publicationsDescription || '',
        facultyPublications: data.facultyPublications || [],
        iicTitle: data.iicTitle || '',
        iicDescription: data.iicDescription || '',
        iicAbout: data.iicAbout || '',
        iicInitiatives: data.iicInitiatives || [],
        iicProjectIdeas: data.iicProjectIdeas || '',
        iicPatentsFiled: data.iicPatentsFiled || '',
        iicMembers: data.iicMembers || [],
        facilitiesTitle: data.facilitiesTitle || '',
        facilitiesDescription: data.facilitiesDescription || '',
        researchFacilities: data.researchFacilities || []
      });
    } catch (error) {
      setError('Failed to load research data');
      console.error('Error fetching research:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Faculty Publications Handlers
  const handlePublicationChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      facultyPublications: prev.facultyPublications.map((publication, i) => 
        i === index ? { ...publication, [field]: value } : publication
      )
    }));
  };

  const addPublication = () => {
    setFormData(prev => ({
      ...prev,
      facultyPublications: [...prev.facultyPublications, {
        title: '',
        authors: '',
        journal: '',
        year: new Date().getFullYear().toString(),
        pdfUrl: '',
        abstract: ''
      }]
    }));
  };

  const removePublication = (index) => {
    setFormData(prev => ({
      ...prev,
      facultyPublications: prev.facultyPublications.filter((_, i) => i !== index)
    }));
  };

  // IIC Initiatives Handlers
  const handleInitiativeChange = (index, value) => {
    setFormData(prev => ({
      ...prev,
      iicInitiatives: prev.iicInitiatives.map((initiative, i) => 
        i === index ? value : initiative
      )
    }));
  };

  const addInitiative = () => {
    setFormData(prev => ({
      ...prev,
      iicInitiatives: [...prev.iicInitiatives, '']
    }));
  };

  const removeInitiative = (index) => {
    setFormData(prev => ({
      ...prev,
      iicInitiatives: prev.iicInitiatives.filter((_, i) => i !== index)
    }));
  };

  // IIC Members Handlers
  const handleMemberChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      iicMembers: prev.iicMembers.map((member, i) => 
        i === index ? { ...member, [field]: value } : member
      )
    }));
  };

  const addMember = () => {
    setFormData(prev => ({
      ...prev,
      iicMembers: [...prev.iicMembers, {
        name: '',
        position: '',
        department: '',
        email: '',
        phone: ''
      }]
    }));
  };

  const removeMember = (index) => {
    setFormData(prev => ({
      ...prev,
      iicMembers: prev.iicMembers.filter((_, i) => i !== index)
    }));
  };

  // Research Facilities Handlers
  const handleFacilityChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      researchFacilities: prev.researchFacilities.map((facility, i) => 
        i === index ? { ...facility, [field]: value } : facility
      )
    }));
  };

  const addFacility = () => {
    setFormData(prev => ({
      ...prev,
      researchFacilities: [...prev.researchFacilities, {
        title: '',
        icon: 'fas fa-circle',
        color: 'bg-crimson',
        description: ''
      }]
    }));
  };

  const removeFacility = (index) => {
    setFormData(prev => ({
      ...prev,
      researchFacilities: prev.researchFacilities.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      if (researchData?.id) {
        await apiService.updateResearch(researchData.id, formData);
        setSuccess('Research page updated successfully!');
      } else {
        await apiService.createResearch(formData);
        setSuccess('Research page created successfully!');
      }
      
      await fetchResearchData();
    } catch (error) {
      setError('Failed to save research data');
      console.error('Error saving research:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-slate">
          <i className="fas fa-spinner fa-spin mr-2 text-crimson"></i>
          Loading research data...
        </div>
      </div>
    );
  }

  return (
    <div className="font-sans">
      <div className="mb-8">
        <h1 className="text-3xl font-sans font-bold text-slate mb-2">
          Research & Development Page Management
        </h1>
        <p className="text-gray-600">
          Manage the content for the Research & Development page including publications, innovation cell, and facilities.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          <i className="fas fa-exclamation-circle mr-2"></i>
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-6">
          <i className="fas fa-check-circle mr-2"></i>
          {success}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Banner Section */}
        <div className="bg-white rounded-lg border border-border_gray shadow-sm p-6">
          <h2 className="text-xl font-sans font-semibold text-slate mb-4">Banner Section</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate mb-2">Banner Title</label>
              <input
                type="text"
                value={formData.bannerTitle}
                onChange={(e) => handleInputChange('bannerTitle', e.target.value)}
                className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                placeholder="Research & Development"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate mb-2">Banner Subtitle</label>
              <textarea
                value={formData.bannerSubtitle}
                onChange={(e) => handleInputChange('bannerSubtitle', e.target.value)}
                className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                rows={2}
                placeholder="Innovation Hub | Academic Excellence | Cutting-Edge Research"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate mb-2">Banner Image</label>
              <ImageUpload
                value={formData.bannerImage}
                onChange={(value) => handleInputChange('bannerImage', value)}
                placeholder="Banner background image filename"
              />
            </div>
          </div>
        </div>

        {/* Faculty Publications Section */}
        <div className="bg-white rounded-lg border border-border_gray shadow-sm p-6">
          <h2 className="text-xl font-sans font-semibold text-slate mb-4">Faculty Publications Section</h2>
          
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate mb-2">Section Title</label>
                <input
                  type="text"
                  value={formData.publicationsTitle}
                  onChange={(e) => handleInputChange('publicationsTitle', e.target.value)}
                  className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                  placeholder="Faculty Publications"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate mb-2">Section Description</label>
                <input
                  type="text"
                  value={formData.publicationsDescription}
                  onChange={(e) => handleInputChange('publicationsDescription', e.target.value)}
                  className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                  placeholder="Research papers and scholarly contributions by our faculty members."
                />
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-4">
                <label className="block text-sm font-medium text-slate">Faculty Publications</label>
                <button
                  type="button"
                  onClick={addPublication}
                  className="px-4 py-2 bg-crimson text-white rounded-lg hover:bg-crimson/90 flex items-center gap-2 text-sm"
                >
                  <i className="fas fa-plus"></i>
                  Add Publication
                </button>
              </div>
              
              <div className="space-y-4">
                {formData.facultyPublications.map((publication, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4 relative">
                    <button
                      type="button"
                      onClick={() => removePublication(index)}
                      className="absolute top-2 right-2 text-red-500 hover:text-red-700"
                    >
                      <i className="fas fa-times-circle"></i>
                    </button>
                    
                    <div className="space-y-3">
                      <div>
                        <input
                          type="text"
                          value={publication.title}
                          onChange={(e) => handlePublicationChange(index, 'title', e.target.value)}
                          className="w-full px-3 py-2 border border-border_gray rounded text-sm"
                          placeholder="Publication Title"
                        />
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <input
                          type="text"
                          value={publication.authors}
                          onChange={(e) => handlePublicationChange(index, 'authors', e.target.value)}
                          className="px-3 py-2 border border-border_gray rounded text-sm"
                          placeholder="Authors"
                        />
                        <input
                          type="text"
                          value={publication.journal}
                          onChange={(e) => handlePublicationChange(index, 'journal', e.target.value)}
                          className="px-3 py-2 border border-border_gray rounded text-sm"
                          placeholder="Journal/Conference"
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <input
                          type="text"
                          value={publication.year}
                          onChange={(e) => handlePublicationChange(index, 'year', e.target.value)}
                          className="px-3 py-2 border border-border_gray rounded text-sm"
                          placeholder="Year"
                        />
                        <div>
                          <DocumentUpload
                            value={publication.pdfUrl}
                            onChange={(value) => handlePublicationChange(index, 'pdfUrl', value)}
                            placeholder="Upload PDF"
                          />
                        </div>
                      </div>

                      <div>
                        <textarea
                          value={publication.abstract}
                          onChange={(e) => handlePublicationChange(index, 'abstract', e.target.value)}
                          className="w-full px-3 py-2 border border-border_gray rounded text-sm"
                          placeholder="Abstract (optional)"
                          rows={3}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Innovation Cell Section */}
        <div className="bg-white rounded-lg border border-border_gray shadow-sm p-6">
          <h2 className="text-xl font-sans font-semibold text-slate mb-4">Innovation Cell Section</h2>
          
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate mb-2">IIC Title</label>
                <input
                  type="text"
                  value={formData.iicTitle}
                  onChange={(e) => handleInputChange('iicTitle', e.target.value)}
                  className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                  placeholder="Engineering College Innovation Cell"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate mb-2">IIC Description</label>
                <input
                  type="text"
                  value={formData.iicDescription}
                  onChange={(e) => handleInputChange('iicDescription', e.target.value)}
                  className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                  placeholder="Fostering innovation and entrepreneurship among students and faculty."
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate mb-2">About IIC</label>
              <textarea
                value={formData.iicAbout}
                onChange={(e) => handleInputChange('iicAbout', e.target.value)}
                className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                rows={4}
                placeholder="Description about the Innovation Cell..."
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate mb-2">Project Ideas Count</label>
                <input
                  type="text"
                  value={formData.iicProjectIdeas}
                  onChange={(e) => handleInputChange('iicProjectIdeas', e.target.value)}
                  className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                  placeholder="50+"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate mb-2">Patents Filed Count</label>
                <input
                  type="text"
                  value={formData.iicPatentsFiled}
                  onChange={(e) => handleInputChange('iicPatentsFiled', e.target.value)}
                  className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                  placeholder="15"
                />
              </div>
            </div>

            {/* IIC Initiatives */}
            <div>
              <div className="flex justify-between items-center mb-4">
                <label className="block text-sm font-medium text-slate">IIC Initiatives</label>
                <button
                  type="button"
                  onClick={addInitiative}
                  className="px-4 py-2 bg-crimson text-white rounded-lg hover:bg-crimson/90 flex items-center gap-2 text-sm"
                >
                  <i className="fas fa-plus"></i>
                  Add Initiative
                </button>
              </div>
              
              <div className="space-y-2">
                {formData.iicInitiatives.map((initiative, index) => (
                  <div key={index} className="flex gap-2">
                    <input
                      type="text"
                      value={initiative}
                      onChange={(e) => handleInitiativeChange(index, e.target.value)}
                      className="flex-1 px-3 py-2 border border-border_gray rounded text-sm"
                      placeholder="Initiative description"
                    />
                    <button
                      type="button"
                      onClick={() => removeInitiative(index)}
                      className="px-2 py-2 text-red-500 hover:text-red-700"
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* IIC Members */}
            <div>
              <div className="flex justify-between items-center mb-4">
                <label className="block text-sm font-medium text-slate">IIC Committee Members</label>
                <button
                  type="button"
                  onClick={addMember}
                  className="px-4 py-2 bg-crimson text-white rounded-lg hover:bg-crimson/90 flex items-center gap-2 text-sm"
                >
                  <i className="fas fa-plus"></i>
                  Add Member
                </button>
              </div>
              
              <div className="space-y-4">
                {formData.iicMembers.map((member, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4 relative">
                    <button
                      type="button"
                      onClick={() => removeMember(index)}
                      className="absolute top-2 right-2 text-red-500 hover:text-red-700"
                    >
                      <i className="fas fa-times-circle"></i>
                    </button>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      <input
                        type="text"
                        value={member.name}
                        onChange={(e) => handleMemberChange(index, 'name', e.target.value)}
                        className="px-3 py-2 border border-border_gray rounded text-sm"
                        placeholder="Name"
                      />
                      <input
                        type="text"
                        value={member.position}
                        onChange={(e) => handleMemberChange(index, 'position', e.target.value)}
                        className="px-3 py-2 border border-border_gray rounded text-sm"
                        placeholder="Position"
                      />
                      <input
                        type="text"
                        value={member.department}
                        onChange={(e) => handleMemberChange(index, 'department', e.target.value)}
                        className="px-3 py-2 border border-border_gray rounded text-sm"
                        placeholder="Department"
                      />
                      <input
                        type="email"
                        value={member.email}
                        onChange={(e) => handleMemberChange(index, 'email', e.target.value)}
                        className="px-3 py-2 border border-border_gray rounded text-sm"
                        placeholder="Email (optional)"
                      />
                      <input
                        type="text"
                        value={member.phone}
                        onChange={(e) => handleMemberChange(index, 'phone', e.target.value)}
                        className="px-3 py-2 border border-border_gray rounded text-sm"
                        placeholder="Phone (optional)"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Research Facilities Section */}
        <div className="bg-white rounded-lg border border-border_gray shadow-sm p-6">
          <h2 className="text-xl font-sans font-semibold text-slate mb-4">Research Facilities Section</h2>
          
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate mb-2">Facilities Title</label>
                <input
                  type="text"
                  value={formData.facilitiesTitle}
                  onChange={(e) => handleInputChange('facilitiesTitle', e.target.value)}
                  className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                  placeholder="Research Facilities"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate mb-2">Facilities Description</label>
                <input
                  type="text"
                  value={formData.facilitiesDescription}
                  onChange={(e) => handleInputChange('facilitiesDescription', e.target.value)}
                  className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                  placeholder="State-of-the-art research infrastructure supporting cutting-edge research."
                />
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-4">
                <label className="block text-sm font-medium text-slate">Research Facilities</label>
                <button
                  type="button"
                  onClick={addFacility}
                  className="px-4 py-2 bg-crimson text-white rounded-lg hover:bg-crimson/90 flex items-center gap-2 text-sm"
                >
                  <i className="fas fa-plus"></i>
                  Add Facility
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {formData.researchFacilities.map((facility, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4 relative">
                    <button
                      type="button"
                      onClick={() => removeFacility(index)}
                      className="absolute top-2 right-2 text-red-500 hover:text-red-700"
                    >
                      <i className="fas fa-times-circle"></i>
                    </button>
                    
                    <div className="space-y-3">
                      <input
                        type="text"
                        value={facility.title}
                        onChange={(e) => handleFacilityChange(index, 'title', e.target.value)}
                        className="w-full px-3 py-2 border border-border_gray rounded text-sm"
                        placeholder="Facility Title"
                      />
                      
                      <div className="grid grid-cols-2 gap-2">
                        <input
                          type="text"
                          value={facility.icon}
                          onChange={(e) => handleFacilityChange(index, 'icon', e.target.value)}
                          className="px-3 py-2 border border-border_gray rounded text-sm"
                          placeholder="Icon class (e.g., fas fa-microscope)"
                        />
                        <select
                          value={facility.color}
                          onChange={(e) => handleFacilityChange(index, 'color', e.target.value)}
                          className="px-3 py-2 border border-border_gray rounded text-sm"
                        >
                          <option value="bg-crimson">Crimson</option>
                          <option value="bg-gold">Gold</option>
                          <option value="bg-slate">Slate</option>
                          <option value="bg-blue-500">Blue</option>
                          <option value="bg-green-500">Green</option>
                        </select>
                      </div>

                      <textarea
                        value={facility.description}
                        onChange={(e) => handleFacilityChange(index, 'description', e.target.value)}
                        className="w-full px-3 py-2 border border-border_gray rounded text-sm"
                        placeholder="Facility description"
                        rows={2}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-3 bg-crimson text-white rounded-lg font-medium hover:bg-crimson/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {saving ? (
              <>
                <i className="fas fa-spinner fa-spin"></i>
                Saving...
              </>
            ) : (
              <>
                <i className="fas fa-save"></i>
                Save Research Content
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ResearchEdit;